import React from 'react';

/**
 * Inserts a React element (typically a link) into a message string by replacing
 * the specified link text with the provided link element.
 *
 * This utility is used to convert messages with placeholder text (e.g., "{link}")
 * that have been formatted with intl.formatMessage() into JSX that includes
 * interactive elements like anchor tags.
 *
 * @param message - The formatted message string containing the link text
 * @param linkText - The text in the message that should be replaced with the link element
 * @param linkElement - The React element (typically an <a> tag) to insert
 * @returns A React node with the link element inserted, or the original message if linkText is not found
 *
 * @example
 * const message = intl.formatMessage({ id: 'Click {link} to learn more' }, { link: 'here' });
 * const link = <a href="/help">here</a>;
 * const result = insertLinkIntoMessage(message, 'here', link);
 * // Returns: ['Click ', <a href="/help">here</a>, ' to learn more']
 */
export const insertLinkIntoMessage = (
    message: string,
    linkText: string,
    linkElement: React.ReactElement
): React.ReactNode => {
    if (!linkText) {
        return message;
    }
    const parts = message.split(linkText);
    if (parts.length === 1) {
        return message;
    }
    const nodes: React.ReactNode[] = [];
    parts.forEach((part, idx) => {
        nodes.push(part);
        if (idx < parts.length - 1) {
            nodes.push(React.cloneElement(linkElement, { key: `link-${idx}` }));
        }
    });
    return nodes;
};

